import React, { useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { jobsEndpoint } from "../constants"; // adjust path to your constants file

// ─── API ────────────────────────────────────────────────────────────────────
const apiClient = axios.create({
  baseURL: "aiaura.co.uk", // adjust to your API base URL
});
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Token ${token}`;
  return config;
});

const STATUS_CHOICES = [
  "APPLIED",
  "REVIEWING",
  "SHORTLISTED",
  "INTERVIEWED",
  "OFFERED",
  "REJECTED",
  "ACCEPTED",
];

// ─── Sub-components ──────────────────────────────────────────────────────────

function SectionLabel({ title, subtitle }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[#009C97] text-xs font-bold uppercase tracking-widest">
        {title}
      </span>
      {subtitle && <p className="text-[#535862] text-xs">{subtitle}</p>}
    </div>
  );
}

function FileDropZone({ label, files, onAdd, onRemove, accept, hint }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    onAdd(dropped);
  };

  return (
    <div className="flex flex-col gap-2">
      <span className="text-[#414651] text-sm font-medium">{label}</span>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer border-2 border-dashed rounded-xl p-6 flex flex-col items-center gap-2 transition-colors duration-200 ${
          dragging ? "border-[#009C97] bg-[#F0FFFE]" : "border-[#D5D7DA] bg-[#FAFAFA] hover:border-[#009C97] hover:bg-[#F0FFFE]"
        }`}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 16L12 8M12 8L9 11M12 8L15 11" stroke="#009C97" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M20 16.5C21.4 15.4 22 13.7 22 12C22 8.7 19.3 6 16 6C15.8 6 15.6 5.9 15.5 5.7C14.4 3.7 12.3 2 9.5 2C5.9 2 3 4.9 3 8.5C3 10.3 3.7 11.9 4.9 13.1" stroke="#009C97" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
        <p className="text-sm text-[#535862]">
          <span className="text-[#009C97] font-semibold">Click to upload</span> or drag and drop
        </p>
        {hint && <p className="text-xs text-[#A0A4AB]">{hint}</p>}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          className="hidden"
          onChange={(e) => onAdd(Array.from(e.target.files))}
        />
      </div>

      {files.length > 0 && (
        <div className="flex flex-col gap-2 mt-1">
          {files.map((file, i) => (
            <div key={i} className="flex items-center justify-between bg-white border border-[#E9EAEB] rounded-lg px-3 py-2">
              <div className="flex items-center gap-2 overflow-hidden">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M9.33 1.33H4A1.33 1.33 0 002.67 2.67v10.67A1.33 1.33 0 004 14.67h8a1.33 1.33 0 001.33-1.33V5.33L9.33 1.33z" stroke="#009C97" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9.33 1.33v4h4" stroke="#009C97" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="text-xs text-[#414651] font-medium truncate">{file.name}</span>
                <span className="text-xs text-[#A0A4AB] shrink-0">
                  {(file.size / 1024).toFixed(0)} KB
                </span>
              </div>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onRemove(i); }}
                className="text-[#A0A4AB] hover:text-red-500 transition-colors ml-2 shrink-0"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ApplicantEditPage() {
  const { applicantId } = useParams();

  // Fields that map 1-to-1 to the serializer
  const [application, setApplication]   = useState("");
  const [formAnswers, setFormAnswers]   = useState("");
  const [status, setStatus]             = useState("APPLIED");

  // File fields — stored as File objects locally before upload
  const [cvFiles, setCvFiles]               = useState([]);
  const [coverLetterFiles, setCoverLetterFiles] = useState([]);
  const [extraFiles, setExtraFiles]         = useState([]);

  // UI state
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [success, setSuccess]   = useState(false);

  const addFiles    = (setter) => (newFiles) => setter((p) => [...p, ...newFiles]);
  const removeFile  = (setter) => (i) => setter((p) => p.filter((_, idx) => idx !== i));

  // ── Upload a single File → returns JobFileContent PK from your backend ──
  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    // jobsEndpoint = "http://127.0.0.1:8000/api/jobs/"
        const response = await fetch(jobsEndpoint + 'upload_files/', {
          method: 'POST',
          headers: {
            'Authorization': `Token ${localStorage.getItem("token")}`,
          },
          body: fd,
        });
    const data = await response.json();
    return data.id; // expects { id: <pk> }
  };

  const uploadAll = async (files) => Promise.all(files.map(uploadFile));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Upload files first, collect PKs
      const [cvIds, coverIds, extraIds] = await Promise.all([
        uploadAll(cvFiles),
        uploadAll(coverLetterFiles),
        uploadAll(extraFiles),
      ]);

      const payload = {
        ...(application  && { application }),
        ...(formAnswers  && { form_answers: formAnswers }),
        status,
        ...(cvIds.length        && { cv: cvIds }),
        ...(coverIds.length     && { cover_letter: coverIds }),
        ...(extraIds.length     && { extra_form_file: extraIds }),
      };

      await axios.patch(`${jobsEndpoint}edit_applicant/${applicantId}/`, payload, {
        headers: {
          Authorization: `Token ${localStorage.getItem("token")}`,
        },
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 4000);
    } catch (err) {
      const data = err?.response?.data;
      setError(
        data?.errors
          ? Object.entries(data.errors).map(([k, v]) => `${k}: ${v}`).join(" | ")
          : data?.error || "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-10 px-4">
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-[#181D27] tracking-tight">
            Edit Application
          </h1>
          <p className="text-[#535862] text-sm mt-1">
            Update your application details below.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-6">

          {/* ── Application ID ──────────────────────────────────────────── */}
          <div className="bg-white border border-[#E9EAEB] rounded-xl shadow-[0px_1px_2px_rgba(10,13,18,0.05)] p-6 flex flex-col gap-5">
            <SectionLabel title="Application" subtitle="Link this to an existing application record." />
            <div className="flex flex-col gap-1.5">
              <label className="text-[#414651] text-sm font-medium">
                Application ID
              </label>
              <input
                type="text"
                value={application}
                onChange={(e) => setApplication(e.target.value)}
                placeholder="e.g. 123"
                className="outline-none w-full text-gray-700 border border-[#D5D7DA] rounded-lg py-2.5 px-3.5 text-sm focus:border-[#009C97] focus:ring-1 focus:ring-[#009C97] transition"
              />
            </div>
          </div>

          {/* ── Status ──────────────────────────────────────────────────── */}
          <div className="bg-white border border-[#E9EAEB] rounded-xl shadow-[0px_1px_2px_rgba(10,13,18,0.05)] p-6 flex flex-col gap-5">
            <SectionLabel title="Status" subtitle="Current stage of this application." />
            <div className="flex flex-wrap gap-2">
              {STATUS_CHOICES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-150 ${
                    status === s
                      ? "bg-[#009C97] text-white border-[#009C97]"
                      : "bg-white text-gray-600 border-gray-300 hover:border-[#009C97] hover:text-[#009C97]"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* ── Form Answers ─────────────────────────────────────────────── */}
          <div className="bg-white border border-[#E9EAEB] rounded-xl shadow-[0px_1px_2px_rgba(10,13,18,0.05)] p-6 flex flex-col gap-5">
            <SectionLabel title="Form Answers" subtitle="Your responses to the application form questions." />
            <textarea
              rows={5}
              value={formAnswers}
              onChange={(e) => setFormAnswers(e.target.value)}
              placeholder="Enter your answers here..."
              className="outline-none resize-none w-full text-gray-700 border border-[#D5D7DA] rounded-lg py-2.5 px-3.5 text-sm focus:border-[#009C97] focus:ring-1 focus:ring-[#009C97] transition"
            />
          </div>

          {/* ── File Uploads ─────────────────────────────────────────────── */}
          <div className="bg-white border border-[#E9EAEB] rounded-xl shadow-[0px_1px_2px_rgba(10,13,18,0.05)] p-6 flex flex-col gap-6">
            <SectionLabel title="Documents" subtitle="Upload your CV, cover letter, and any extra files." />

            <FileDropZone
              label="CV / Resume"
              files={cvFiles}
              onAdd={addFiles(setCvFiles)}
              onRemove={removeFile(setCvFiles)}
              accept=".pdf,.doc,.docx"
              hint="PDF, DOC, DOCX — max 5MB each"
            />

            <div className="h-px bg-[#E9EAEB]" />

            <FileDropZone
              label="Cover Letter"
              files={coverLetterFiles}
              onAdd={addFiles(setCoverLetterFiles)}
              onRemove={removeFile(setCoverLetterFiles)}
              accept=".pdf,.doc,.docx"
              hint="PDF, DOC, DOCX — max 5MB each"
            />

            <div className="h-px bg-[#E9EAEB]" />

            <FileDropZone
              label="Extra Files"
              files={extraFiles}
              onAdd={addFiles(setExtraFiles)}
              onRemove={removeFile(setExtraFiles)}
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              hint="Any supporting documents or portfolio files"
            />
          </div>

          {/* ── Feedback ─────────────────────────────────────────────────── */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm font-medium">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-3 text-sm font-medium flex items-center gap-2">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M13.3 4L6 11.3 2.7 8" stroke="#16a34a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Application updated successfully!
            </div>
          )}

          {/* ── Actions ──────────────────────────────────────────────────── */}
          <div className="flex items-center justify-end gap-3 pb-10">
            <button
              type="button"
              onClick={() => window.history.back()}
              className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 bg-[#009C97] text-white rounded-lg text-sm font-semibold hover:bg-[#007f7b] transition disabled:opacity-60 flex items-center gap-2"
            >
              {loading && (
                <svg className="animate-spin" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="7" r="5.5" stroke="white" strokeWidth="1.5" strokeDasharray="8 6"/>
                </svg>
              )}
              {loading ? "Saving…" : "Save changes"}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}